/**
 * Avatar - Core Component (Enhanced Wrapper)
 *
 * This component wraps shadcn/ui Avatar with comprehensive size, shape,
 * and status indicator support.
 *
 * Enhancements over shadcn/ui:
 * - Size variants: xs (24px), sm (32px), md (40px), lg (48px), xl (64px), 2xl (80px)
 * - Shape options: circle (default), square
 * - Status indicator: online (green), offline (gray), away (yellow), busy (red)
 * - Ring/border option with customizable color
 * - Automatic fallback text sizing based on avatar size
 *
 * Sub-components:
 * - Avatar: Container with size, shape, status, ring props
 * - AvatarImage: The actual image (with lazy loading)
 * - AvatarFallback: Displayed when image fails to load (usually initials)
 *
 * @see {@link file://./../ui/avatar.tsx} for the underlying implementation
 */
import { forwardRef, type ComponentProps, type ComponentRef } from 'react';
import {
  Avatar as ShadcnAvatar,
  AvatarImage as ShadcnAvatarImage,
  AvatarFallback as ShadcnAvatarFallback,
} from '@/app/_components/ui/avatar';
import { cn } from '@/app/_lib/utils';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type AvatarStatus = 'online' | 'offline' | 'away' | 'busy';
export type AvatarShape = 'circle' | 'square';

export interface AvatarProps
  extends Omit<ComponentProps<typeof ShadcnAvatar>, 'size'> {
  /**
   * The size of the avatar
   * @default 'md'
   */
  size?: AvatarSize;
  /**
   * The shape of the avatar
   * @default 'circle'
   */
  shape?: AvatarShape;
  /**
   * Status indicator to display
   */
  status?: AvatarStatus;
  /**
   * Whether to show a ring/border around the avatar
   * @default false
   */
  ring?: boolean;
  /**
   * Custom ring color class
   */
  ringColor?: string;
}

export type AvatarImageProps = ComponentProps<typeof ShadcnAvatarImage>;

export interface AvatarFallbackProps
  extends ComponentProps<typeof ShadcnAvatarFallback> {
  /**
   * The size of the fallback text/content
   * Automatically adjusted based on avatar size
   */
  size?: AvatarSize;
}

const getSizeStyles = (size: AvatarSize) => {
  switch (size) {
    case 'xs':
      return 'size-6';
    case 'sm':
      return 'size-8';
    case 'md':
      return 'size-10';
    case 'lg':
      return 'size-12';
    case 'xl':
      return 'size-16';
    case '2xl':
      return 'size-20';
    default:
      return 'size-10';
  }
};

const getFallbackTextSize = (size: AvatarSize) => {
  switch (size) {
    case 'xs':
      return 'text-2xs';
    case 'sm':
      return 'text-xs';
    case 'md':
      return 'text-sm';
    case 'lg':
      return 'text-base';
    case 'xl':
      return 'text-lg';
    case '2xl':
      return 'text-xl';
    default:
      return 'text-sm';
  }
};

const getShapeStyles = (shape: AvatarShape) => {
  switch (shape) {
    case 'circle':
      return 'rounded-full';
    case 'square':
      return 'rounded-md';
    default:
      return 'rounded-full';
  }
};

const getStatusStyles = (status: AvatarStatus) => {
  switch (status) {
    case 'online':
      return 'bg-status-online';
    case 'offline':
      return 'bg-status-offline';
    case 'away':
      return 'bg-status-away';
    case 'busy':
      return 'bg-status-busy';
    default:
      return '';
  }
};

const getStatusIndicatorSize = (size: AvatarSize) => {
  switch (size) {
    case 'xs':
      return 'size-1.5';
    case 'sm':
      return 'size-2';
    case 'md':
      return 'size-2.5';
    case 'lg':
      return 'size-3';
    case 'xl':
      return 'size-4';
    case '2xl':
      return 'size-5';
    default:
      return 'size-2.5';
  }
};

export const Avatar = forwardRef<
  ComponentRef<typeof ShadcnAvatar>,
  AvatarProps
>(
  (
    {
      size = 'md',
      shape = 'circle',
      status,
      ring = false,
      ringColor = 'ring-background',
      className,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div className="relative inline-block">
        <ShadcnAvatar
          ref={ref}
          className={cn(
            getSizeStyles(size),
            getShapeStyles(shape),
            ring && `ring-2 ${ringColor}`,
            className
          )}
          {...props}
        >
          {children}
        </ShadcnAvatar>
        {status && (
          <span
            className={cn(
              'absolute bottom-0 right-0 block rounded-full ring-2 ring-background',
              getStatusStyles(status),
              getStatusIndicatorSize(size)
            )}
            aria-label={`Status: ${status}`}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export const AvatarImage = forwardRef<
  ComponentRef<typeof ShadcnAvatarImage>,
  AvatarImageProps
>((props, ref) => {
  return <ShadcnAvatarImage ref={ref} {...props} />;
});

AvatarImage.displayName = 'AvatarImage';

export const AvatarFallback = forwardRef<
  ComponentRef<typeof ShadcnAvatarFallback>,
  AvatarFallbackProps
>(({ size = 'md', className, ...props }, ref) => {
  return (
    <ShadcnAvatarFallback
      ref={ref}
      className={cn(getFallbackTextSize(size), className)}
      {...props}
    />
  );
});

AvatarFallback.displayName = 'AvatarFallback';

export default Avatar;
